import api from '@/services/api'
import type { CloserLeadFormData } from '@/types'
import { emptyCloserLeadFormData } from '@/utils/closerLeadDraft'
import {
  validateContactRegistration,
  validateEmpresaRegistration,
  validateRequiredCloserFields,
  type EmpresaOcupacao,
} from '@/utils/leadRegistrationValidation'
import { normalizePhoneDigits } from '@/utils/formatPhone'

export type EventLeadQuickFields = {
  contato: string
  telefone: string
  empresa: string
  empresaId?: string
  observacoes?: string
}

type EmpresaSearchRow = {
  id: string
  nome: string
  ocupacao?: EmpresaOcupacao | null
}

export function buildEventLeadQuickPayload(fields: EventLeadQuickFields): CloserLeadFormData {
  return {
    ...emptyCloserLeadFormData,
    contato: fields.contato.trim(),
    telefone: fields.telefone.trim(),
    empresa: fields.empresa.trim(),
    empresaId: fields.empresaId,
    origem: 'Evento',
    observacoes: fields.observacoes?.trim() ?? '',
  }
}

export function validateEventLeadQuickFields(
  fields: EventLeadQuickFields,
): Record<string, string> {
  return validateRequiredCloserFields({
    contato: fields.contato,
    telefone: fields.telefone,
    empresa: fields.empresa,
  })
}

export async function validateEventLeadQuickContact(
  telefone: string,
  empresaId?: string,
): Promise<Record<string, string>> {
  return validateContactRegistration(telefone, undefined, undefined, empresaId)
}

async function searchEmpresaExactMatch(name: string): Promise<EmpresaSearchRow | null> {
  const trimmed = name.trim()
  if (trimmed.length < 2) return null

  try {
    const { data } = await api.get(`/empresas/v2-search?q=${encodeURIComponent(trimmed)}`)
    const list = (data?.data ?? []) as EmpresaSearchRow[]
    return (
      list.find((row) => row.nome?.trim().toLowerCase() === trimmed.toLowerCase()) ?? null
    )
  } catch {
    return null
  }
}

export type EventLeadQuickEmpresaValidation = {
  errors: Record<string, string>
  empresaId?: string
}

/** Valida empresa existente (busca + regras de prospecção ativa). */
export async function validateEventLeadQuickEmpresa(
  empresa: string,
  empresaId?: string,
  options?: { skipDuplicateLookup?: boolean },
): Promise<EventLeadQuickEmpresaValidation> {
  if (options?.skipDuplicateLookup) {
    return { errors: {} }
  }

  const resolvedId = empresaId ?? (await searchEmpresaExactMatch(empresa))?.id
  if (!resolvedId) return { errors: {} }

  const registrationErrors = await validateEmpresaRegistration(resolvedId)
  if (Object.keys(registrationErrors).length > 0) {
    return { errors: registrationErrors, empresaId: resolvedId }
  }

  if (!empresaId) {
    return {
      errors: {
        empresa: 'Empresa já cadastrada — selecione na lista para vincular',
      },
      empresaId: resolvedId,
    }
  }

  return { errors: {}, empresaId: resolvedId }
}

export function shouldValidateEventLeadPhone(telefone: string): boolean {
  return normalizePhoneDigits(telefone).length >= 8
}
